import { Link } from "react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { disableMfa, enableMfa, startMfaSetup } from "../../../auth";
import { useTherapist } from "../../../../context/useTherapist";

export default function MfaSetup() {
  const { t } = useTranslation();
  const { therapist, setTherapist } = useTherapist();
  const [step, setStep] = useState<"initial" | "setup" | "success" | "disable_confirm">("initial");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successType, setSuccessType] = useState<"setup" | "disable">("setup");

  const handleStartSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const setup = await startMfaSetup();
      setQrCode(setup.qr_code_base64);
      setStep("setup");
    } catch {
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      await enableMfa(totpCode);
      
      if (therapist) {
          setTherapist({
              ...therapist,
              mfa_enabled: true
          });
      }
      
      setSuccessType("setup");
      setStep("success");
    } catch {
      setError(t("auth.errorInvalidMfa"));
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    setLoading(true);
    setError(null);
    try {
      await disableMfa(password);
      
      if (therapist) {
        setTherapist({
          ...therapist,
          mfa_enabled: false
        });
      }
      
      setSuccessType("disable");
      setStep("success");
      setPassword("");
    } catch {
      setError(t("auth.errorInvalidPassword"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-white/5 rounded-3xl border border-gray-200/50 dark:border-white/10 p-8 shadow-theme-sm">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
          <Shield className="w-6 h-6" />
        </div>
        <div>
          <h4 className="text-xl font-bold text-content-primary">
            {t("profile.mfa.title")}
          </h4>
          <p className="text-sm text-content-tertiary">
            {therapist?.mfa_enabled ? t("profile.mfa.enabled") : t("profile.mfa.disabled")}
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === "initial" && (
          <motion.div
            key="initial"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <p className="text-gray-600 dark:text-gray-300">
              {t("profile.mfa.description")}
            </p>
            {therapist?.mfa_enabled ? (
              <Button variant="outline" className="text-error-600 hover:text-error-700 hover:bg-error-50 dark:hover:bg-error-500/10 border-error-200" onClick={() => setStep("disable_confirm")}>
                {t("profile.mfa.disable")}
              </Button>
            ) : (
              <Button onClick={handleStartSetup} loading={loading}>
                {t("profile.mfa.setup")}
              </Button>
            )}
          </motion.div>
        )}

        {step === "disable_confirm" && (
          <motion.div
            key="disable_confirm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="p-4 rounded-2xl bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/20 text-warning-700 dark:text-warning-400 text-sm">
              {t("profile.mfa.disableWarning")}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                {t("auth.password")}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-100 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <Button variant="outline" className="flex-1" onClick={() => { setStep("initial"); setPassword(""); setError(null); }}>
                {t("common.cancel")}
              </Button>
              <Button 
                variant="primary" 
                className="flex-1 bg-error-600 hover:bg-error-700 border-none" 
                onClick={handleDisableMfa} 
                loading={loading}
                disabled={!password}
              >
                {t("profile.mfa.confirmDisable")}
              </Button>
            </div>
          </motion.div>
        )}

        {step === "setup" && (
          <motion.div
            key="setup"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
              <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-300">
                {t("profile.mfa.qrCodeDesc")}
              </p>
              {qrCode && (
                <div className="p-4 bg-white rounded-xl shadow-sm">
                  <img src={`data:image/png;base64,${qrCode}`} alt="MFA QR Code" className="w-48 h-48" />
                </div>
              )}
              <Link
                to="/google-authenticator-guide"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400 hover:underline mt-2"
              >
                {t("profile.mfa.guideLink")}
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mfaCode">
                {t("profile.mfa.enterCode")}
              </Label>
              <Input
                id="mfaCode"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="000 000"
                className="text-center text-2xl tracking-widest font-bold"
              />
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-100 dark:border-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <Button variant="outline" className="flex-1" onClick={() => setStep("initial")}>
                {t("common.cancel")}
              </Button>
              <Button className="flex-1" onClick={handleVerifyAndEnable} loading={loading} disabled={totpCode.length < 6}>
                {t("profile.mfa.verifyAndEnable")}
              </Button>
            </div>
          </motion.div>
        )}

        {step === "success" && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-4 py-8"
          >
            <div className="w-16 h-16 rounded-full bg-success-500/10 text-success-500 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h5 className="text-xl font-bold text-content-primary">
              {successType === "setup" ? t("profile.mfa.success") : t("profile.mfa.disableSuccess")}
            </h5>
            <Button onClick={() => { setStep("initial"); setError(null); }}>
              {t("common.close")}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
